/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtom, useStateXSelector } from '@cloudio-saas/statex';
import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import NoDataFound from 'api/NoDataFound';
import { AnyManager, ItemID, Platform } from 'componentregistry';
import { hideDefaultPropsAtom } from 'designer/atoms';
import React from 'react';
import PropertiesSection from './PropertiesSection';
import { visiblePropertiesSelector } from './state';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  noMatch: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    margin: 40,
  },
}));

interface Props {
  activeItemId: ItemID;
  appUid: string;
  hmr: number;
  mgr: AnyManager;
  pageId: ItemID;
  preventCollapse?: boolean;
  visibleProps?: string[];
  platform: Platform;
}

export default function PropertiesForm({
  activeItemId,
  appUid,
  hmr,
  mgr,
  pageId,
  preventCollapse,
  visibleProps,
  platform,
}: Props): React.ReactElement {
  const classes = useStyles();
  const [propsByCategory, filter] = useStateXSelector(
    visiblePropertiesSelector,
    {
      params: { appUid, pageId, itemId: activeItemId, type: mgr.type },
      props: { visibleProps, platform },
    },
  );
  const sections = Object.keys(propsByCategory).sort((a, b) => {
    if (a === b) return 0;
    if (a === 'Common') {
      return 1;
    }
    if (b === 'Common') {
      return -1;
    }
    if (a === 'Look & Feel') {
      return 1;
    }
    if (b === 'Look & Feel') {
      return -1;
    }
    return a.localeCompare(b);
  });
  const [hideDefaultProps, setHideDefaultProps] =
    useStateXAtom(hideDefaultPropsAtom);
  return (
    <div className={classes.root}>
      {sections.length ? null : (
        <NoDataFound
          msg={
            <>
              <Typography>
                {filter
                  ? `No properties found matching '${filter}'!`
                  : hideDefaultProps
                  ? 'No properties changed!'
                  : 'Nothing to configure!'}
              </Typography>
              {hideDefaultProps && !pageId.startsWith('$sample$') ? (
                <Button
                  onClick={() => {
                    setHideDefaultProps(false);
                  }}
                >
                  {filter ? 'Include Default Props' : 'Show All Props'}
                </Button>
              ) : null}
            </>
          }
        />
      )}
      {sections.map((section) => {
        const properties = propsByCategory[section];
        return (
          <PropertiesSection
            activeItemId={activeItemId}
            appUid={appUid}
            hmr={hmr}
            key={section}
            mgr={mgr}
            pageId={pageId}
            preventCollapse={preventCollapse}
            properties={properties}
            section={section}
            platform={platform}
          />
        );
      })}
    </div>
  );
}
